import { memo } from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { CheckCircle as ApproveIcon } from '@mui/icons-material';
import type { Board, LedgerSummary } from '../../types';

export interface PlayerStatsBannerProps {
  summary: LedgerSummary;
  userId: string;
  board: Board;
  approvedSquares: number;
  userClaimedCount: number;
}

export const PlayerStatsBanner = memo<PlayerStatsBannerProps>(({
  summary,
  userId,
  board,
  approvedSquares,
  userClaimedCount,
}) => {
  // Find user's balance in the summary (balances may be null from backend)
  const userBalance = (summary.balances || []).find(b => b.userId === userId);

  // If no balance info and no approved squares, don't show anything
  if (!userBalance && approvedSquares === 0) {
    return null;
  }

  const totalCost = approvedSquares * board.pricePerSquare;
  const totalPaid = userBalance?.totalPaid || 0;
  const amountOwed = totalCost - totalPaid;
  const totalWinnings = userBalance?.totalEarned || 0;
  const isPaid = amountOwed <= 0;

  return (
    <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Squares */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Squares:</Typography>
          <Chip
            size="small"
            label={`${userClaimedCount}/${approvedSquares} claimed`}
            color={userClaimedCount === approvedSquares ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>

        {/* Cost & Payment */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Cost:</Typography>
          <Typography variant="body2" fontWeight="bold">${totalCost.toFixed(2)}</Typography>
          {totalPaid > 0 && (
            <Typography variant="body2" color="text.secondary">
              (${totalPaid.toFixed(2)} paid)
            </Typography>
          )}
        </Box>

        {/* Amount Owed */}
        {!isPaid && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">You owe:</Typography>
            <Chip
              size="small"
              label={`$${amountOwed.toFixed(2)}`}
              color="warning"
            />
          </Box>
        )}
        {isPaid && (
          <Chip size="small" label="Paid" color="success" icon={<ApproveIcon />} />
        )}

        {/* Winnings */}
        {totalWinnings > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Winnings:</Typography>
            <Chip
              size="small"
              label={`$${totalWinnings.toFixed(2)}`}
              color="success"
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
});

PlayerStatsBanner.displayName = 'PlayerStatsBanner';

export default PlayerStatsBanner;
